import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import { useAuth } from '../context/AuthContext';
import { getBackofficeSidebarItems } from '../features/backoffice/shell/backofficeSidebar';
import type { EmployeeProfile, MeResponse } from '../api/client';
import PhoneInput from '../components/form/PhoneInput';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:3001';

function EmployeeProfilePage() {
  const {
    isAuthenticated,
    authorizedFetch,
    auth,
    registerCurrentDevicePasskey,
    listPasskeys,
    renamePasskey,
    deletePasskey,
  } = useAuth();
  const location = useLocation();
  const [me, setMe] = useState<MeResponse>(null);
  const [profile, setProfile] = useState<EmployeeProfile>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeys, setPasskeys] = useState<Array<{ id: string; nickname?: string | null; createdAt: string }>>([]);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    if (!isAuthenticated) return;

    (async () => {
      try {
        const [meRes, profileRes] = await Promise.all([
          authorizedFetch(`${API_BASE}/auth/me`),
          authorizedFetch(`${API_BASE}/auth/profile`),
        ]);

        if (!meRes.ok || !profileRes.ok) {
          throw new Error(t('employee.load_error'));
        }

        const meData = await meRes.json();
        const profileText = await profileRes.text();
        const profileData = profileText ? JSON.parse(profileText) : null;
        setMe(meData);
        setProfile(profileData);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t('employee.load_error'),
        );
      }
    })();
  }, [isAuthenticated, authorizedFetch, t]);

  useEffect(() => {
    if (!isAuthenticated) return;
    void (async () => {
      const rows = await listPasskeys();
      setPasskeys(rows.map((r) => ({ id: r.id, nickname: r.nickname, createdAt: r.createdAt })));
    })();
  }, [isAuthenticated, listPasskeys]);

  const handleChange =
    (field: keyof NonNullable<EmployeeProfile>) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setProfile((prev) =>
          prev
            ? {
                ...prev,
                [field]: e.target.value,
              }
            : {
                id: '',
                fullName: '',
                roleTitle: '',
                phone: '',
                [field]: e.target.value,
              } as EmployeeProfile,
        );
      };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await authorizedFetch(`${API_BASE}/auth/profile/employee`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName: profile.fullName,
          phone: profile.phone,
        }),
      });
      if (!res.ok) {
        throw new Error(t('employee.save_error'));
      }
      const updated = await res.json();
      setProfile(updated);
      setSuccess(t('employee.save_success'));
      setIsEditing(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('employee.save_error'),
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) return;
    if (newPassword !== confirmPassword) {
      setError(t('employee.password_mismatch', 'Passwords do not match.'));
      return;
    }
    setSavingPassword(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await authorizedFetch(`${API_BASE}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const message =
          (body as { message?: string }).message
          || t('employee.password_change_error', 'Unable to change password.');
        throw new Error(message);
      }
      setSuccess(t('employee.password_change_success', 'Password updated successfully.'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('employee.password_change_error', 'Unable to change password.'),
      );
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSetupPasskey = async () => {
    setPasskeyBusy(true);
    setError(null);
    try {
      await registerCurrentDevicePasskey();
      const rows = await listPasskeys();
      setPasskeys(rows.map((r) => ({ id: r.id, nickname: r.nickname, createdAt: r.createdAt })));
      setSuccess('Passkey saved for this staff account.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to register passkey.');
    } finally {
      setPasskeyBusy(false);
    }
  };

  const handleRenamePasskey = async (id: string) => {
    try {
      await renamePasskey(id, renameValue.trim() || undefined);
      setPasskeys((prev) => prev.map((p) => (p.id === id ? { ...p, nickname: renameValue.trim() || null } : p)));
      setRenamingId(null);
      setRenameValue('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rename failed.');
    }
  };

  const handleDeletePasskey = async (id: string) => {
    try {
      await deletePasskey(id);
      setPasskeys((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed.');
    }
  };

  const isAdmin = auth?.type === 'ADMIN';
  const canReadDocs = isAdmin || (auth?.permissions || []).includes('DOC:READ');
  const sidebarItems = isAdmin
    ? getBackofficeSidebarItems(t)
    : [
        { label: t('employee.sidebar_profile'), path: '/workspace/profile', active: true },
        ...(canReadDocs
          ? [{ label: t('backoffice.sidebar_docs', 'Documentation'), path: '/workspace/docs', active: false }]
          : []),
      ];

  const roleLabel =
    me?.roles && me.roles.length > 0
      ? me.roles.map((r) => r.name).join(', ')
      : (me?.type || 'EMPLOYEE');

  return (
    <MainLayout
      withSidebar
      sidebarItems={sidebarItems}
      activePath={location.pathname}
    >
      <main className="workspace-page">
        <section>
          <h1 className="mb-1 text-lg font-semibold tracking-tight text-slate-900 sm:text-xl">
            {t('employee.title')}
          </h1>
          <p className="text-xs text-slate-500 sm:text-sm">
            {t('employee.subtitle')}
          </p>
        </section>

        <section className="workspace-panel">
          {!isAuthenticated && (
            <p className="text-sm text-slate-600">
              {t('employee.sign_in_required')}
            </p>
          )}

          {isAuthenticated && (
            <div className="grid gap-6 md:grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)]">
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-semibold text-slate-900">
                      {t('employee.profile_section_title')}
                    </h2>
                    <p className="text-xs text-slate-500">
                      {t('employee.profile_section_subtitle')}
                    </p>
                  </div>
                  {!isEditing && (
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        setSuccess(null);
                        setIsEditing(true);
                      }}
                      className="rounded-full border border-slate-300 px-3 py-1 text-xs font-medium text-slate-700 hover:border-slate-400"
                    >
                      {t('employee.edit_button')}
                    </button>
                  )}
                </div>

                {error && (
                  <p className="mb-2 text-xs text-red-500">
                    {error}
                  </p>
                )}
                {success && (
                  <p className="mb-2 text-xs text-emerald-600">
                    {success}
                  </p>
                )}

                {isEditing ? (
                  <form className="space-y-4" onSubmit={handleSave}>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5 text-sm">
                        <label
                          className="block text-xs font-medium text-slate-700"
                          htmlFor="fullName"
                        >
                          {t('employee.field_full_name')}
                        </label>
                        <input
                          id="fullName"
                          type="text"
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                          value={profile?.fullName ?? ''}
                          onChange={handleChange('fullName')}
                        />
                      </div>

                      <div className="space-y-1.5 text-sm">
                        <label
                          className="block text-xs font-medium text-slate-700"
                          htmlFor="role"
                        >
                          {t('employee.readonly_role_label')}
                        </label>
                        <input
                          id="role"
                          type="text"
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                          value={roleLabel}
                          disabled
                        />
                      </div>
                    </div>

                    <PhoneInput
                      id="phone"
                      label={t('employee.field_phone')}
                      value={profile?.phone ?? ''}
                      onChange={(val) =>
                        setProfile((prev) =>
                          prev
                            ? {
                                ...prev,
                                phone: val,
                              }
                            : {
                                id: '',
                                fullName: '',
                                roleTitle: '',
                                phone: val,
                              } as EmployeeProfile,
                        )
                      }
                    />

                    <div className="flex items-center gap-2">
                      <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/80 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {saving ? t('employee.saving_label') : t('employee.save_button')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsEditing(false);
                          setError(null);
                          setSuccess(null);
                        }}
                        className="text-xs font-medium text-slate-500 hover:text-slate-700"
                      >
                        {t('employee.cancel_button')}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-4 text-xs sm:text-sm">
                    <div>
                      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                        {t('employee.readonly_name_label')}
                      </div>
                      <div className="mt-1 text-slate-900">
                        {profile?.fullName || t('employee.not_set')}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                        {t('employee.readonly_role_label')}
                      </div>
                      <div className="mt-1 text-slate-900">
                        {roleLabel}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                        {t('employee.readonly_phone_label')}
                      </div>
                      <div className="mt-1 text-slate-900">
                        {profile?.phone || t('employee.not_set')}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-700">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-900">
                    {t('employee.account_email_label')}
                  </span>
                  <span className="text-slate-700">
                    {me?.email}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-900">
                    {t('employee.account_role_label')}
                  </span>
                  <span className="rounded-full bg-slate-900/5 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-800">
                    {roleLabel}
                  </span>
                </div>
                <div className="mt-4 border-t border-slate-200 pt-4">
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {t('employee.security_section_title')}
                    </h3>
                    {!showPasswordForm && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowPasswordForm(true);
                          setError(null);
                          setSuccess(null);
                        }}
                        className="rounded-full border border-slate-300 px-3 py-1 text-[11px] font-medium text-slate-700 hover:border-slate-400"
                        >
                          {t('employee.password_open_button')}
                      </button>
                    )}
                  </div>
                  {showPasswordForm && (
                    <form className="space-y-3" onSubmit={handlePasswordChange}>
                      <div className="space-y-1.5">
                        <label
                          className="block text-[11px] font-medium text-slate-700"
                          htmlFor="currentPassword"
                        >
                          {t('employee.current_password_label')}
                        </label> 
                        <input
                          id="currentPassword"
                        name="current-password"
                          type="password"
                          autoComplete="current-password"
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label
                          className="block text-[11px] font-medium text-slate-700"
                          htmlFor="newPassword"
                        >
                          {t('employee.new_password_label')}
                        </label>
                        <input
                          id="newPassword"
                        name="new-password"
                          type="password"
                          autoComplete="new-password"
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label
                          className="block text-[11px] font-medium text-slate-700"
                          htmlFor="confirmPassword"
                        >
                          {t('employee.confirm_password_label')}
                        </label>
                        <input
                          id="confirmPassword"
                        name="new-password-confirm"
                          type="password"
                          autoComplete="new-password"
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="submit"
                          disabled={savingPassword}
                          className="inline-flex items-center justify-center rounded-lg bg-slate-900 px-3 py-2 text-[11px] font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {savingPassword
                            ? t('employee.password_saving_label')
                            : t('employee.password_save_button')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowPasswordForm(false);
                            setCurrentPassword('');
                            setNewPassword('');
                            setConfirmPassword('');
                          }}
                          className="text-[11px] font-medium text-slate-500 hover:text-slate-700"
                        >
                          {t('employee.cancel_button')}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
                <div className="mt-4 border-t border-slate-200 pt-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Passkey sign-in
                  </h3>
                  <p className="mt-1 text-[11px] text-slate-600">
                    Enroll this device for faster staff/admin sign-in while keeping email verification and alerts enabled.
                  </p>
                  <button
                    type="button"
                    onClick={handleSetupPasskey}
                    disabled={passkeyBusy}
                    className="mt-3 inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-[11px] font-semibold text-slate-800 hover:bg-slate-100 disabled:opacity-60"
                  >
                    {passkeyBusy ? 'Setting up passkey...' : 'Add this device as passkey'}
                  </button>
                  {passkeys.length > 0 ? (
                    <div className="mt-3 space-y-2 rounded-lg border border-slate-200 bg-white p-3 text-[11px]">
                      {passkeys.map((item) => (
                        <div key={item.id} className="flex items-center justify-between gap-2 rounded border border-slate-200 p-2">
                          <div className="truncate">{item.nickname || 'Unnamed passkey'}</div>
                          <div className="flex items-center gap-2">
                            {renamingId === item.id ? (
                              <>
                                <input
                                  className="w-36 rounded border border-slate-300 px-2 py-1"
                                  value={renameValue}
                                  onChange={(e) => setRenameValue(e.target.value)}
                                />
                                <button type="button" onClick={() => void handleRenamePasskey(item.id)} className="text-sky-700">
                                  Save
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                onClick={() => {
                                  setRenamingId(item.id);
                                  setRenameValue(item.nickname || '');
                                }}
                                className="text-sky-700"
                              >
                                Rename
                              </button>
                            )}
                            <button type="button" onClick={() => void handleDeletePasskey(item.id)} className="text-red-700">
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
    </MainLayout>
  );
}

export default EmployeeProfilePage;

